import { inject, Injectable } from '@angular/core';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { HostPrefillService } from '@workflow/ecp-workflow/dynamic-app/service/host-prefill.service';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { OaoPageHelper } from '../../helper/oao-page.helper';
import { CUDLConnect } from '../../../dynamic-app/component/building-block/app-prefill/app-prefill.type';
import { StatefulService } from '@app/service/stateful.service';
import { FlowService } from '@workflow/class/flow.service';
import { CookieService, TokenService } from '@cudirect/shared-ui-library';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OaoCustomFunctionsService {
  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly flowHelper: OaoPageHelper,
    private readonly l360Service: L360Service,
    protected readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly prefillService: HostPrefillService,
    private readonly flowService: FlowService,
    private readonly cookieService: CookieService,
    private readonly tokenService: TokenService,
    private readonly userService: UserService,
    private readonly recaptcha: ReCaptchaV3Service,
    private readonly applicationApiService: ApplicationApiService,
    private readonly lenderService: LenderService,
    private readonly workflowLoader: WorkflowLoaderService
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public oaoFlowInitializer = (context: LogicFnContext): void => {
    const membershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value = membershipId;
    this.flowHelper.resetRequestedDoc();
    this.flowHelper.ValidateAllOAOPage();
    this.flowHelper.oaoNavigationInProgressApp();
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public identityContinuetext = (context: LogicFnContext): string => {
    const isEmptyPIDReport = this.statefulService.wrap('ss:oaoMemberInformation/isEmptyPIDReport').value;
    return isEmptyPIDReport ? 'Continue' : 'Verify my identity';
  };

  /** Loads static Accounst and Service response and caching it
   * @param bundleValue saved for enableMidappDecision
   */
  public LoadStaticAccounts = (context: LogicFnContext, bundleValue: boolean): void => {
    const enableMidappDecision = typeof bundleValue === 'boolean' ? bundleValue : true;
    // Pre-loading Static Accounts and Services when Mid App decision is disabled
    if (!enableMidappDecision) {
      // before making api call for runautomated decision, lets clear the cache. Update it again, Once we get response.
      this.l360Service.cachedAccountAndService = null;
      this.l360Service.GetEligibileProductsFromL360().subscribe((data) => {
        if (data.isSuccess) this.l360Service.cachedAccountAndService = data.data;
        this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/isAccountsServiceCompleted').value = true;
      });
    }
  };

  /** Returns review page id based on midAppDecisionEnabled
   * @param bundleValue saved for enableMidappDecision
   * @returns Review pageId for page
   */
  public getAccountsReviewPage = (context: LogicFnContext, pageId: string, bundleValue: boolean): string => {
    const enableMidappDecision = typeof bundleValue === 'boolean' ? bundleValue : true;
    return enableMidappDecision ? pageId : 'oao-application-review';
  };

  /**
   * Load Homebanking Prefill Data for OAO
   */
  public tryOAOLoadPrefillData = (context: LogicFnContext): Promise<boolean> => {
    return new Promise((resolve) => {
      this.prefillService.flowId = 'oao';
      const prefillDataWrap = this.statefulService.wrap('mem:home-banking-prefill-data');
      const prefillData = prefillDataWrap.valueString;

      if (!prefillData) {
        this.workflowLoader.end();
        resolve(true);
        return;
      }

      prefillDataWrap.value = null;
      this.statefulService.wrap('api:hostPrefillToken').value = null;

      const parsedData = JSON.parse(prefillData) as CUDLConnect;
      if (!parsedData) {
        this.workflowLoader.end();
        resolve(true);
        return;
      }

      this.statefulService.wrap('mem:oao-prefill-inprogress').value = true;
      let pageIterationId: number;
      const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
      let apiPrimaryRoot = 'primaryApplicant';
      if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
        apiPrimaryRoot = 'businessInformation/responsibleParties/#';
        pageIterationId = 0;
      }
      if (this.flowHelper.flow.config.streamlined) {
        this.workflowLoader.start('Connecting to your profile...', {
          animationId: 'documentscan',
          overlayMode: 'card',
          segments: this.flowHelper.flow.config.streamlinedSkipPages.length
        });
      }
      this.prefillService.checkAndAddApplicant(`api:oaoMemberInformation/${apiPrimaryRoot}`).then(() => {
        this.prefillService.updatePrefillDataForOao(
          parsedData,
          () => {
            this.statefulService.wrap('api:hostPrefillToken').value = null;
            this.statefulService.wrap('mem:oao-prefill-inprogress').value = false;
            const checkForCondensedView = () => {
              const [previewCardId] = context.page.cards.filter((t) => t.config.customCardType === 'autofill-preview').map((t) => t.id);
              if (
                this.flowHelper.flow.config.streamlined ||
                context.page.cards.filter((t) => t.visible && t.helper.flatVisibleFields.findIndex((f) => f.required && !f.valid) !== -1)
                  .length !== 0
              ) {
                this.statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = true;
              } else {
                this.statefulService.wrap('mem:edit-' + previewCardId, null, context.page?.pageIterationId).value = false;
              }
            };
            setTimeout(checkForCondensedView, 0);
            resolve(true);
          },
          apiPrimaryRoot,
          pageIterationId
        );
      });
    });
  };

  /**
   * Prefill OAO Business Info Page details
   */
  public prefillBusinessInfoData = () => {
    const isloading = this.statefulService.wrap('mem:isloading').value as boolean;
    const otuToken = this.statefulService.wrap('api:hostPrefillToken').value as string;
    if (isloading !== true && otuToken) {
      this.statefulService.wrap('mem:isloading').value = true;
      this.prefillService.prefillBusinessInfo(otuToken);
    }
  };

  /** load custom field to memory cache */
  public getOAOCustomFields = (context: LogicFnContext, membershipID: string) => {
    this.l360Service.getOAODynamicCustomFields({ membershipID }).subscribe({
      next: (res) => {
        if (res.isSuccess && res.data) this.l360Service.cachedCustomfields = res.data.applicationCustomFields;
      }
    });
  };

  /**
   * This will perform registration up front - when in a prefill scenario
   * we want to register as soon as possible, but we can't do it until the bundle has the needed data.
   * This should be called after updating the bundle with the prefill data.
   * @param context The context of the function scope.
   * @returns A promise to resolve
   */
  private ensureRegistered(context: LogicFnContext): Promise<boolean> {
    return new Promise((resolve) => {
      const { isBusiness, apiPrimaryRoot } = this.getPrimaryRoot();
      this.handleAuthTypeCookie(context, apiPrimaryRoot);

      /** check whether the user already registered */
      this.tokenService.configureCache('sessionStorage', 'ecp-user-session');
      const isRegisteredApplication = this.statefulService.wrap('api:isRegisteredApplication').value;
      const isAuthenticated = this.userService.isAuthenticated;
      if (isRegisteredApplication || isAuthenticated) {
        const cachedToken = this.tokenService.getCachedToken();
        let tokenType = 'unknown';
        if (cachedToken) {
          tokenType = this.tokenService.determineTokenType();
        }

        if (cachedToken && tokenType === 'arcDX') {
          this.silentRegisterWithRecaptcha(context, isBusiness, true).then(
            () => {
              // TODO: Redirect to Accounts and Service.
              // this.goToOAOIncompletePage(context);
              resolve(true);
            },
            () => resolve(false)
          );
        } else {
          //
          this.setValueToCondense(isBusiness);
          resolve(true);
        }
      } else {
        this.silentRegisterWithRecaptcha(context, isBusiness, null).then(
          () => {
            // TODO: Redirect to Accounts and Service.
            // this.goToOAOIncompletePage(context);
            resolve(true);
          },
          () => resolve(false)
        );
      }
    });
  }

  /**
   * Gets the primary applicant based on the application type. For OAO only.
   * @returns The string value for the base of an OAO application primary applicant. And if this is business or not
   */
  private getPrimaryRoot(): { isBusiness: boolean; apiPrimaryRoot: string } {
    let isBusiness = false;
    // 1. identify Membership Type 1. Personal = 1, 2. Youth = 2, 3. Business = 3,
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    let apiPrimaryRoot = 'api:oaoMemberInformation/primaryApplicant';
    if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
      apiPrimaryRoot = 'api:oaoMemberInformation/businessInformation/responsibleParties/0';
      isBusiness = true;
    }

    return { isBusiness, apiPrimaryRoot };
  }

  /**
   * Sets a cookie to indicate the authentication type used for the application.
   * This is read later in arcDX and fed into Login Widget.
   * @param context The function logic context for the scope
   * @param apiPrimaryRoot The primary root for the application primary applicant
   */
  private handleAuthTypeCookie(context: LogicFnContext, apiPrimaryRoot: string): void {
    const mobile = this.statefulService.wrap(`${apiPrimaryRoot}/mobileNumber`).value;
    const isPersonalInfo = context.page.id === 'oao-application-fields';
    const isUserLoggedIn = this.userService.isAuthenticated;
    if (mobile && isPersonalInfo && !isUserLoggedIn) {
      this.cookieService.setCookie('arcDXAuthType', 'phone', 365, '/');
      this.cookieService.setCookie('arcDXReturningUser', 'true', 90, '/');
    } else if (!mobile && isPersonalInfo && !isUserLoggedIn) {
      this.cookieService.setCookie('arcDXAuthType', 'email', 365, '/');
      this.cookieService.setCookie('arcDXReturningUser', 'true', 90, '/');
    }
  }

  /**
   * Gets the data payload for the registration call.
   * @returns The payload object for registration.
   */
  private getRegisterPayload() {
    const { apiPrimaryRoot } = this.getPrimaryRoot();
    const email = this.statefulService.wrap(`${apiPrimaryRoot}/email`).valueStringOnly?.toLowerCase();
    const cellPhone = this.statefulService.wrap(`${apiPrimaryRoot}/mobileNumber`).valueStringOnly;
    const formattedCellPhone = cellPhone ? cellPhone.replace(/\D/g, '') : cellPhone;
    return {
      email,
      confirmEmail: email,
      isRegisteredWithCuid: false,
      registeredWithSSO: false,
      loginType: 'OAO',
      cellPhone: formattedCellPhone,
      firstName: this.statefulService.wrap(`${apiPrimaryRoot}/firstName`).valueStringOnly,
      lastName: this.statefulService.wrap(`${apiPrimaryRoot}/lastName`).valueStringOnly,
      isSilentRegistration: true
    };
  }

  /**
   * Enables condensed view if not streamlined personal.
   * @param isBusiness If business application type - logic is different
   */
  private setValueToCondense(isBusiness: boolean): void {
    if (isBusiness) {
      this.statefulService.wrap('mem:edit-oao-business-res-personal-preview-#', null, 0).value = false;
    } else if (!this.flowHelper.flow.config.streamlined) {
      this.statefulService.wrap('mem:edit-oao-primary-personal-preview').value = false;
      this.statefulService.wrap('mem:edit-oao-youth-primary-personal-preview').value = false;
    }
  }

  /**
   * The silent registration happens here, if it fails we trigger an MFA step up.
   * @param context The function context
   * @param isBusiness Is this a business application
   * @param isInternal Is this internal? Think this is for registration type
   * @returns A promise, a dream, and the clothes off my back.
   */
  private silentRegisterWithRecaptcha(context: LogicFnContext, isBusiness: boolean, isInternal: boolean = false): Promise<void> {
    const postData = this.getRegisterPayload();
    const { apiPrimaryRoot } = this.getPrimaryRoot();

    return new Promise((resolve, reject) => {
      if (
        this.lenderService.lenderData.enableRecaptcha &&
        this.statefulService.wrap(`${apiPrimaryRoot}/autoFillOption`).valueString !== 'Prefill' &&
        !isInternal
      ) {
        this.recaptcha.execute('silent_register').subscribe({
          next: (token) => {
            this.silentRegisterCustom(context, isBusiness, postData, token).then(resolve, reject);
          },
          error: () => {
            this.statefulService.wrap('ss:showMFAVerification').value = true;
            reject();
          }
        });
      } else {
        this.silentRegisterCustom(context, isBusiness, postData).then(resolve, reject);
      }
    });
  }
  /**
   * This method used to regsiter the user and next to incomplete OAO page
   * @param context
   */
  private silentRegisterCustom(context: LogicFnContext, isBusiness, postData, token?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const user = this.userService.user || ({} as UserInfo);
      user.email = postData.email;
      user.cellPhone = postData.cellPhone;
      user.mfaVerifyMethod = user.cellPhone ? 'Phone' : 'Email_Code';
      this.userService.user = user;
      this.applicationApiService.silentRegistration(
        postData,
        token,
        (response) => {
          if (!response.isSuccess) {
            // Person is high risk, show MFA verification
            this.statefulService.wrap('ss:showMFAVerification').value = true;
            reject(response);
            return;
          }
          this.userService.isExistingUser = true;

          if (!!token) {
            this.statefulService.wrap('api:isRecaptchaVerified').value = true;
          }
          this.statefulService.wrap('api:isRegisteredApplication').value = true;
          this.statefulService.wrap('ss:trigger-appId-creation-event').value = true; // Triggers GA event once appId available
          // this.SetUser(response);
          this.goToOAOIncompletePage(context);
          this.setValueToCondense(isBusiness);
          resolve();
        },
        () => {
          this.setValueToCondense(isBusiness);
          reject();
        }
      );
    });
  }

  /**
   * Goes to the next incomplete pages.
   * @param context The function context for the scope
   */
  private goToOAOIncompletePage(context: LogicFnContext): void {
    context?.defaultNavigationFn && context.defaultNavigationFn();
  }
}
